/* prettier-ignore-file */

/**
 * Format phone number for display (e.g. (123) 456-7890)
 * Handles cleaning and progressive formatting
 */
export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return "";
  const cleaned = ("" + phoneNumber).replace(/\D/g, "");
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return "(" + match[1] + ") " + match[2] + "-" + match[3];
  }
  return phoneNumber;
};

/**
 * Format phone number progressively as user types
 */
export const formatPhoneNumberProgressive = (value) => {
  if (!value) return value;
  // Convert to string to handle numbers or other types
  const phoneNumber = ("" + value).replace(/[^\d]/g, "");
  const phoneNumberLength = phoneNumber.length;
  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

/**
 * Clean phone number to digits only
 */
export const cleanPhoneNumber = (phoneNumber) => {
  return ("" + phoneNumber).replace(/\D/g, "");
};

/**
 * Format phone for display as xxx-xxx-xxxx (e.g. 555-123-4567)
 */
export const formatPhoneNumberXXX = (phoneNumber) => {
  if (!phoneNumber) return "";
  const cleaned = ("" + phoneNumber).replace(/\D/g, "");
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
  return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
};

/**
 * Format phone progressively as user types: xxx-xxx-xxxx (max 10 digits)
 */
export const formatPhoneNumberProgressiveDashes = (value) => {
  if (!value) return "";
  const digits = ("" + value).replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/**
 * Format address object to string
 */
export const formatAddress = (addressObj) => {
  if (!addressObj) return "";
  const { address, city, state, zip } = addressObj;
  return `${address}, ${city}, ${state} ${zip}`;
};

/**
 * Format address object to lines array
 */
export const formatAddressLines = (addressObj) => {
  if (!addressObj) return [];
  const { address, city, state, zip } = addressObj;
  return [address, `${city}, ${state} ${zip}`];
};

/**
 * Apply a percentage to a number: returns number * (percent / 100).
 * e.g. calculatePercentage(1000, 80) => 800 (80% of 1000)
 */
export const calculatePercentage = (number, percent) => {
  if (percent == null || percent === undefined) return 0;
  return Math.round(Number(number) * (Number(percent) / 100));
};

/**
 * Convert dollars to cents
 */
export const dollarsToCents = (dollars) => {
  return Math.round(parseFloat(dollars) * 100);
};

/**
 * Convert cents to dollars
 */
export const centsToDollars = (cents) => {
  return (cents / 100).toFixed(2);
};

/**
 * Format cents as USD currency string
 */
export const formatCentsAsUSD = (cents) => {
  if (cents === null || cents === undefined) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
};

/**
 * Format dollars as USD currency string
 */
export const formatDollarsAsUSD = (dollars) => {
  if (dollars === null || dollars === undefined) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(dollars);
};

/**
 * Format dollars as USD currency string, always showing .00 (for Order Summary)
 * @param {number} dollars - Dollar amount
 * @returns {string} Formatted currency string (e.g., "$34.56" or "$34.00")
 */
export const formatDollarsAsUSDWithCents = (dollars) => {
  if (dollars === null || dollars === undefined) return "$0.00";
  const num = Number(dollars);
  if (isNaN(num)) return "$0.00";
  return `$${num.toFixed(2)}`;
};

/**
 * Format time from 24-hour to 12-hour with AM/PM
 * @param {string} time24 - Time in 24-hour format (HH:MM)
 * @returns {string} Time in 12-hour format with AM/PM
 */
export const formatTime = (time24) => {
  if (!time24) return "";
  const [hours, minutes] = time24.split(":");
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

/**
 * Get today's date with time set to midnight
 * @returns {Date} Today's date at midnight
 */
export const getToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

/**
 * Get tomorrow's date with time set to midnight
 * @returns {Date} Tomorrow's date at midnight
 */
export const getTomorrow = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  return tomorrow;
};

/**
 * Helper to parse Firestore timestamp safely
 * Handles Firestore Timestamp objects, JS Date objects, and ISO strings
 */
export const parseFirestoreTimestamp = (timestamp) => {
  if (!timestamp) return null;
  
  // Handle Firestore Timestamp (has toMillis method)
  if (timestamp && typeof timestamp.toMillis === 'function') {
    return timestamp.toMillis();
  }
  
  // Handle Firestore Timestamp-like object (seconds, nanoseconds)
  if (timestamp && typeof timestamp.seconds === 'number') {
    return timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000;
  }
  
  // Handle JS Date object
  if (timestamp instanceof Date) {
    return timestamp.getTime();
  }
  
  // Handle string or number
  const d = new Date(timestamp);
  return isNaN(d.getTime()) ? null : d.getTime();
};

/**
 * Helper to format phone number to (xxx) xxx-xxxx
 * Same as formatPhoneNumber but separate export if needed for clarity
 */
export const formatPhoneNumberDashes = (phoneNumber) => {
  if (!phoneNumber) return "";
  const cleaned = ("" + phoneNumber).replace(/\D/g, "");
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return "(" + match[1] + ") " + match[2] + "-" + match[3];
  }
  return phoneNumber;
};

/**
 * Helper to capitalize words
 */
export const capitalizeWords = (str) => {
  if (!str) return "";
  return str.replace(/\b\w/g, (l) => l.toUpperCase());
};

/**
 * Normalize a person's name for customer-facing messages: first letter of
 * each word upper-case, the rest lower-case ("nathan bubash" -> "Nathan
 * Bubash"). Mirrors capitalizeName in functions/index.js so SMS and email
 * built in the browser match the ones built by the backend.
 */
export const capitalizeName = (name) => {
  if (!name) return "";
  return String(name)
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

/**
 * Capitalize just the first letter of a string, leaving the rest as typed.
 * Unlike React Native's autoCapitalize prop (a virtual-keyboard hint that
 * desktop browsers with a physical keyboard largely ignore), this actually
 * transforms the value.
 */
export const capitalizeFirstLetter = (str) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Helper to parse Firestore date
 */
export const parseFirestoreDate = (dateField) => {
  if (!dateField) return null;
  if (dateField.toDate && typeof dateField.toDate === "function") {
    return dateField.toDate();
  }
  if (dateField instanceof Date) return dateField;
  if (dateField.seconds) return new Date(dateField.seconds * 1000);
  return new Date(dateField);
};

/**
 * Get TZ day key for comparison (YYYY-MM-DD)
 * Uses America/New_York timezone
 */
export const getTZDayKey = (ms) => {
  try {
    if (ms == null) return null;
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date(ms));
    const y = parts.find((p) => p.type === "year")?.value;
    const m = parts.find((p) => p.type === "month")?.value;
    const d = parts.find((p) => p.type === "day")?.value;
    return y && m && d ? `${y}-${m}-${d}` : null;
  } catch (e) {
    return null;
  }
};
